import { jsx as c, jsxs as K } from "react/jsx-runtime";
import { useLink as pe } from "@made-people/grease-link";
import { createEventEmitter as be } from "@made-people/grease-event-emitter";
import { createContext as h, useContext as l, useMemo as f, useRef as g, useEffect as y, useReducer as L, useCallback as me } from "react";
import { z as r } from "zod";
import { moneySchema as v } from "@made-people/grease-money";
import { withZod as G } from "@made-people/react-prop-guard";
import { useSeenInViewport as ve } from "@made-people/react-use-seen-in-viewport";
import { overlayEventEmitter as fe } from "@made-people/blueprint-overlay";
const E = be(), A = h(void 0), Dt = (e) => /* @__PURE__ */ c(A.Provider, { value: e, children: e.children }), Pe = () => {
  const e = l(A);
  return e || { additionalData: void 0 };
}, D = r.object({
  attributeLabel: r.string().describe("The localized label of the attribute"),
  attributeKey: r.string().describe("The key of the attribute"),
  // For some reason, z.unknown() makes the field optional
  value: r.custom((e) => typeof e < "u", "Must be set").describe("The value of the attribute"),
  valueLabel: r.string().describe("The localized label of the value")
}).strict(), R = r.object({
  width: r.number().describe("The image width"),
  height: r.number().describe("The image height")
}), ge = r.object({
  type: r.literal("image"),
  src: r.string().describe("The image source"),
  alt: r.string().describe("The image alt text"),
  dimensions: R.optional(),
  attributes: D.array().optional().describe("The image attributes")
}), ye = r.object({
  type: r.literal("video"),
  src: r.string().describe("The video source"),
  poster: r.string().describe("The video poster"),
  dimensions: R.optional(),
  attributes: D.array().optional().describe("The video attributes")
}), Se = r.discriminatedUnion("type", [
  ge,
  ye
]);
function Ie(e) {
  return e.type === "image";
}
const Te = r.object({
  label: r.string().describe("The badge label"),
  badgeType: r.string().describe("The badge type, just a simple string"),
  backgroundColor: r.string().optional().describe("The badge background color"),
  textColor: r.string().optional().describe("The badge text color"),
  imageSrc: r.string().optional().describe("The badge image source")
}), we = r.object({
  name: r.string().describe("The product name"),
  description: r.string().describe("The product description, can be a long text"),
  shortDescription: r.string().describe("The product short description"),
  media: Se.array().describe("The product media (images, videos, etc)"),
  badges: Te.array(),
  specifications: r.record(
    r.string().describe("The group ID of the specifications"),
    r.array(
      r.object({
        label: r.string(),
        value: r.string(),
        icon: r.custom(() => !0).optional()
      })
    )
  ).optional().describe("The product specifications grouped by groupId"),
  brand: r.string().optional().describe("The brand of the product")
}), Ee = r.object({
  categoryId: r.string(),
  slug: r.string(),
  name: r.string()
}), P = r.object({
  productId: r.string().describe("The product ID (could be a database ID)"),
  productKey: r.string().describe("The product business key or ID, sometimes called 'model ID"),
  slug: r.string().describe("The slug part of a product URL"),
  discontinued: r.boolean().describe("Whether the product is discontinued"),
  content: we.describe("All localizable product content"),
  attributes: D.array().describe(
    "The remaining list of product attributes that don't have a place in the required information above"
  ),
  categories: Ee.array().describe("The product categories")
}), De = P, k = r.object({
  base: P,
  siblingProducts: De.array()
}), w = r.object({
  quantity: r.number().describe("The stock quantity"),
  inStock: r.boolean().describe("Whether the item is in stock"),
  warehouseId: r.string().describe("The warehouse ID, useful when doing omnichannel features")
}), ke = r.object({
  percent: r.number().describe("The discount percentage")
}), _ = r.object({
  price: v.describe("The price of the item"),
  priceWithoutDiscount: v.describe(
    "The price of the item without discount"
  )
}), Ve = _.merge(
  r.object({
    periodDays: r.number().describe(
      "The period in days for which this was the lowest price (e.g., 30, 60, 90 days)"
    ).optional(),
    date: r.date().describe("The date when the price was as defined in this object")
  })
), N = _.merge(
  r.object({
    discountInfo: ke.optional().describe(
      "The discount information and original price in case of a discount"
    ),
    previouslyDiscountedPrices: Ve.array().describe("Sorted list of previously discounted prices"),
    discountButNoCampaign: r.boolean().optional().describe(
      "When true, the product has a discounted price but is not part of an active sale campaign. Strikethrough pricing remains visible but sale indicators are suppressed."
    )
  })
), V = r.object({
  sku: r.string().describe(
    "The SKU (Stock Keeping Unit), aka the identifier used by the warehouse to locate the product"
  ),
  inventory: w.describe(
    "The main  consumer facing stock information"
  ),
  additionalInventory: w.array().describe(
    "Additional inventory information, such as stock in other warehouses or physical stores"
  ),
  priceInfo: N.describe("The price information")
}), U = V.merge(
  k.extend({
    dataModel: r.literal("simple"),
    itemId: r.string().describe("The item ID of this product, used for add to cart requests"),
    ean: r.string().optional().describe("The EAN (European Article Number) barcode"),
    outOfStockItemId: r.string().optional().describe(
      "The id that should be used for sending out of stock events, if not added, itemId will be used"
    ),
    isBundle: r.boolean().describe("Whether this product is a bundle")
  })
), Oe = r.object({
  /**
   * The dimension key
   *
   * @example "color" | "size"
   */
  dimensionKey: r.string(),
  /**
   * The translated label or "name" of the dimension
   *
   * @example "Color" | "Färg" | "Size" | "Storlek"
   */
  dimensionLabel: r.string(),
  /**
   * The value of the dimension
   *
   * @example "blue" | "red" | "small" | "large"
   */
  value: r.string(),
  /**
   * The translated label of the dimension value
   *
   * @example "Blue" | "Röd" | "Small" | "Liten"
   */
  valueLabel: r.string()
}), xe = V.extend({
  mainProductId: r.string().describe("The main product ID"),
  mainProductKey: r.string().describe("The main product key"),
  variantId: r.string().describe("The ID of this specific variant"),
  variantKey: r.string().describe("The key of this specific variant"),
  ean: r.string().optional().describe("The EAN (European Article Number) barcode of this variant"),
  outOfStockItemId: r.string().optional().describe(
    "The id that should be used for sending out of stock events, if not added, variantId will be used"
  ),
  label: r.string().describe("The localized label of this variant"),
  dimensions: Oe.array().describe("The dimensions this variant belongs to")
}), $ = k.extend({
  dataModel: r.literal("with_variants"),
  variants: xe.array().describe(
    "The list of purchasable product variants, listed in for instance size pickers.s"
  )
}), Ce = r.object({
  sectionId: r.number(),
  quantity: r.number().describe("The quantity of items in the section"),
  products: r.array(r.union([U, $]))
}), Be = V.extend({
  priceOfItemsOutsideBundle: v.describe(
    "The price of items if bought outside the bundle"
  ),
  discountFromPriceOfItemsOutsideBundle: v.describe(
    "The discount from the price of items if bought outside the bundle"
  ),
  priceMin: v.describe("The minimum price of the bundle"),
  priceMax: v.describe("The maximum price of the bundle")
}), je = k.extend({
  dataModel: r.literal("bundle"),
  preConfiguredBundle: r.boolean().describe(
    "true if the bundle is preConfigured and can't be changed by the customer"
  ),
  bundlePurchaseInfo: Be,
  itemId: r.string().describe("The item ID of this bundle, used for add to cart requests"),
  sections: Ce.array()
}), z = r.discriminatedUnion("dataModel", [
  U,
  $,
  je
]), W = r.object({
  product: z,
  relationType: r.string().describe("The type of relation between the products")
}), Me = r.object({
  product: z,
  relatedProducts: W.array()
});
function q(e, t) {
  return e.find((n) => Object.entries(t).every(
    ([o, s]) => {
      if (!s)
        return !1;
      const i = n.dimensions.find(
        (d) => d.dimensionKey === o
      );
      return (i == null ? void 0 : i.value) === s.dimensionValue;
    }
  ));
}
function Ke(e, t) {
  var n;
  switch (t.type) {
    case "SET_STATE":
      return { ...e, ...t.payload };
    case "SELECT_OPTION":
      if (!((n = e.optionGroups.find(
        (d) => d.dimensionKey === t.payload.dimensionKey
      )) == null ? void 0 : n.options.some(
        (d) => d.dimensionValue === t.payload.option.dimensionValue
      )))
        return console.error(
          `Option '${t.payload.option.dimensionValue}' does not exist in dimension '${t.payload.dimensionKey}'`
        ), e;
      const s = {
        ...e.selectedOptions,
        [t.payload.dimensionKey]: t.payload.option
      }, i = q(e.variants, s);
      return {
        ...e,
        selectedOptions: s,
        selectedVariant: i || e.selectedVariant
      };
    case "CLEAR_OPTIONS":
      return {
        ...e,
        selectedOptions: {},
        selectedVariant: e.initialProductVariant
      };
    default:
      throw new Error(`Invalid action type '${t == null ? void 0 : t.type}'`);
  }
}
const S = () => {
  const e = l(C);
  if (!e)
    throw new Error(
      "useProductVariant must be used within a ProductVariantProvider"
    );
  return e.selectedVariant;
}, F = () => S().dimensions.map((t) => ({
  dimensionKey: t.dimensionKey,
  dimensionLabel: t.dimensionLabel
})), O = () => {
  const e = l(C);
  if (!e)
    throw new Error(
      "useProductVariantState must be used within a ProductVariantProvider"
    );
  return e;
}, Le = () => {
  const e = l(ee);
  if (!e)
    throw new Error(
      "useProductVariantDispatch must be used within a ProductVariantProvider"
    );
  return e;
};
function Ge(e, t, n) {
  return e.map((o) => {
    const s = Object.entries(n).filter(
      ([i, d]) => i !== o.dimensionKey && d !== void 0
    );
    return s.length === 0 ? o : {
      ...o,
      options: o.options.map((i) => {
        const d = t.some((a) => a.dimensions.some(
          (p) => p.dimensionKey === o.dimensionKey && p.value === i.dimensionValue
        ) ? a.inventory.inStock && s.every(
          ([p, b]) => a.dimensions.some(
            (M) => M.dimensionKey === p && M.value === b.dimensionValue
          )
        ) : !1);
        return {
          ...i,
          inventory: {
            ...i.inventory,
            inStock: d
          }
        };
      })
    };
  });
}
const Z = () => {
  const e = O(), t = Le();
  return {
    optionGroups: f(
      () => Ge(
        e.optionGroups,
        e.variants,
        e.selectedOptions
      ),
      [
        e.optionGroups,
        e.variants,
        e.selectedOptions
      ]
    ),
    selectedOptions: e.selectedOptions,
    selectOption: (o, s) => t({
      type: "SELECT_OPTION",
      payload: { dimensionKey: o, option: s }
    }),
    clearOptions: () => t({
      type: "CLEAR_OPTIONS"
    })
  };
}, Ae = () => {
  const e = Z();
  return F().filter(
    ({ dimensionKey: o }) => !e.selectedOptions[o]
  );
}, Re = () => {
  const e = S();
  if (!Ae().length)
    return e.variantId;
}, H = () => O().variants, J = h(void 0), x = (e) => /* @__PURE__ */ c(J.Provider, { value: e.purchaseInfo, children: e.children }), Q = h(void 0), X = (e) => /* @__PURE__ */ c(Q.Provider, { value: e.purchaseStatus, children: e.children }), _e = {
  strictVariantMatching: !1
}, Y = h(_e), Ne = () => l(Y), Ue = (e) => {
  const t = m(), n = S(), o = Re(), s = F(), { strictVariantMatching: i } = Ne(), { selectedOptions: d } = Z(), a = H(), u = {
    sku: n.sku,
    priceInfo: n.priceInfo,
    inventory: n.inventory,
    additionalInventory: n.additionalInventory
  }, p = i ? q(a, d) !== void 0 : !0;
  let b;
  return t.discontinued && !n.inventory.inStock && s.length === 0 ? b = { status: "discontinued" } : o ? n.inventory.inStock && p ? b = {
    status: "available",
    availableItemId: o
  } : b = {
    status: "out-of-stock",
    // Default to variantId if outOfStockItemId is not set
    outOfStockItemId: n.outOfStockItemId ?? n.variantId
  } : b = {
    status: "not-determined",
    missingDimensions: s
  }, /* @__PURE__ */ c(x, { purchaseInfo: u, children: /* @__PURE__ */ c(X, { purchaseStatus: b, children: e.children }) });
}, $e = () => {
  const e = ze(), t = We();
  return f(() => {
    if (!(!t || !e))
      return {
        ...e,
        productData: {
          ...e.productData,
          variant: t
        }
      };
  }, [e == null ? void 0 : e.productData.base.productId, t == null ? void 0 : t.variantId]);
}, ze = () => {
  try {
    return j();
  } catch {
    return;
  }
}, We = () => {
  try {
    return S();
  } catch {
    return;
  }
};
function qe(e, t) {
  const n = g(!0);
  y(() => {
    if (n.current) {
      n.current = !1;
      return;
    }
    return e();
  }, t);
}
const Fe = () => {
  const e = $e();
  return qe(() => {
    e && E.emit("productVariantSelected", e);
  }, [
    e,
    e == null ? void 0 : e.productData.base.productId,
    e == null ? void 0 : e.productData.variant.variantId
  ]), null;
}, C = h(void 0), ee = h(void 0), Ze = (e) => {
  const [t, n] = L(
    Ke,
    e.variantState
  ), o = g(!0);
  return y(() => {
    o.current ? o.current = !1 : e.variantState.variants !== t.variants && n({
      type: "SET_STATE",
      payload: e.variantState
    });
  }, [e.variantState]), /* @__PURE__ */ c(C.Provider, { value: t, children: /* @__PURE__ */ c(ee.Provider, { value: n, children: /* @__PURE__ */ K(Ue, { children: [
    /* @__PURE__ */ c(Fe, {}),
    e.children
  ] }) }) });
};
function He(e) {
  const t = {};
  return e.forEach((n) => {
    n.dimensions.forEach((o) => {
      const s = t[o.dimensionKey], i = (s == null ? void 0 : s.options) ?? [], d = i.find(
        (u) => u.dimensionValue === o.value
      );
      let a;
      d ? a = i.map(
        (u) => u.dimensionValue === o.value && !u.inventory.inStock ? {
          ...u,
          inventory: {
            ...u.inventory,
            inStock: n.inventory.inStock
          }
        } : u
      ) : a = [
        ...i,
        {
          dimensionValue: o.value,
          label: o.valueLabel,
          inventory: n.inventory,
          priceInfo: n.priceInfo
        }
      ], t[o.dimensionKey] = {
        dimensionKey: o.dimensionKey,
        dimensionLabel: o.dimensionLabel,
        options: a
      };
    });
  }), Object.values(t);
}
const Je = (e) => {
  const { optionGroups: t, selectedOptions: n } = f(() => {
    const i = He(e.product.variants), d = Object.fromEntries(
      i.map((a) => [a.dimensionKey, void 0])
    );
    return { optionGroups: i, selectedOptions: d };
  }, [e.product.variants]), o = e.product.variants[0];
  if (!o)
    return console.error(
      "Product must have at least one variant, check your mapper for bugs"
    ), null;
  const s = {
    variants: e.product.variants,
    selectedVariant: o,
    initialProductVariant: o,
    selectedOptions: n,
    optionGroups: t
  };
  return /* @__PURE__ */ c(Ze, { variantState: s, children: e.children });
}, te = () => {
  const e = l(ne);
  if (!e)
    throw new Error("useBundleProduct must be used within a ProductProvider");
  return e;
}, Qe = () => te().bundlePurchaseInfo, I = () => {
  const e = l(oe);
  if (!e)
    throw new Error("useBundleState must be used within a BundleProvider");
  return e;
}, re = h(void 0), Xe = (e) => /* @__PURE__ */ c(re.Provider, { value: e.purchaseStatus, children: e.children }), Ye = ({
  children: e
}) => {
  const t = m(), n = Qe(), o = I(), s = {
    sku: n.sku,
    priceInfo: n.priceInfo,
    inventory: n.inventory,
    additionalInventory: n.additionalInventory
  }, i = Object.values(o.sections).map((u) => ({
    sectionId: u.sectionId,
    availableItemId: u.activeProduct.itemId
  })), d = i.every(
    (u) => typeof u.availableItemId == "string"
  );
  let a;
  return t.discontinued ? a = { status: "discontinued" } : n.inventory.inStock ? d ? a = {
    status: "available",
    sections: i
  } : a = {
    status: "not-determined"
  } : a = {
    status: "out-of-stock"
  }, /* @__PURE__ */ c(x, { purchaseInfo: s, children: /* @__PURE__ */ c(Xe, { purchaseStatus: a, children: e }) });
}, et = (e, t) => {
  const n = e.sections[t.payload.sectionId];
  if (!n)
    throw new Error(`Section '${t.payload.sectionId}' not found`);
  return {
    ...e,
    sections: {
      ...e.sections,
      [t.payload.sectionId]: {
        ...n,
        activeProduct: {
          ...n.activeProduct,
          itemId: t.payload.itemId
        }
      }
    }
  };
}, tt = (e, t) => {
  const n = e.sections[t.payload.sectionId];
  if (!n)
    throw new Error(`Section '${t.payload.sectionId}' not found`);
  if (!n.products.includes(t.payload.product))
    throw new Error(
      `Product '${t.payload.product.base.content.name}' not found in section '${t.payload.sectionId}'`
    );
  return {
    ...e,
    sections: {
      ...e.sections,
      [t.payload.sectionId]: {
        ...n,
        activeProduct: {
          product: t.payload.product,
          itemId: void 0
        }
      }
    }
  };
};
function rt(e, t) {
  switch (t.type) {
    case "SELECT_SECTION_PRODUCT":
      return tt(e, t);
    case "SELECT_BUNDLE_PRODUCT_SIZE":
      return et(e, t);
    default:
      throw new Error(`Invalid action type '${t == null ? void 0 : t.type}'`);
  }
}
const ne = h(
  void 0
), oe = h(
  void 0
), ie = h(void 0), nt = ({
  children: e,
  product: t
}) => {
  const n = {};
  for (const i of t.sections) {
    const d = i.products[0];
    if (!d) {
      console.error(
        `Section '${i.sectionId}' does not have any products`
      );
      continue;
    }
    n[i.sectionId] = {
      sectionId: i.sectionId,
      products: i.products,
      activeProduct: { product: d, itemId: void 0 }
    };
  }
  const [o, s] = L(rt, {
    sections: n
  });
  return /* @__PURE__ */ c(ne.Provider, { value: t, children: /* @__PURE__ */ c(oe.Provider, { value: o, children: /* @__PURE__ */ c(ie.Provider, { value: s, children: /* @__PURE__ */ c(Ye, { children: e }) }) }) });
}, se = h(!1), ot = (e) => /* @__PURE__ */ c(se.Provider, { value: e.isBundle, children: e.children }), kt = () => l(se), it = (e) => {
  const t = {
    sku: e.product.sku,
    priceInfo: e.product.priceInfo,
    inventory: e.product.inventory,
    additionalInventory: e.product.additionalInventory
  };
  let n;
  return e.product.inventory.inStock ? n = {
    status: "available",
    availableItemId: e.product.itemId
  } : e.product.base.discontinued ? n = {
    status: "discontinued"
  } : n = {
    status: "out-of-stock",
    // Default to itemId if outOfStockItemId is not set
    outOfStockItemId: e.product.outOfStockItemId ?? e.product.itemId
  }, /* @__PURE__ */ c(x, { purchaseInfo: t, children: /* @__PURE__ */ c(X, { purchaseStatus: n, children: /* @__PURE__ */ c(ot, { isBundle: e.product.isBundle, children: e.children }) }) });
}, ce = h(void 0), st = () => {
  const e = l(ce);
  if (!e)
    throw new Error(
      "useProductProviderContext must be used within a ProductProvider"
    );
  return e;
}, T = (e) => {
  const t = j(), n = g(null), o = ve({ refElement: n });
  return y(() => {
    o && E.emit("productViewed", t);
  }, [t, o]), /* @__PURE__ */ K("div", { style: { position: "relative" }, children: [
    /* @__PURE__ */ c(
      "div",
      {
        ref: n,
        style: {
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: -1
        }
      }
    ),
    e.children
  ] });
}, de = h(void 0), ct = () => l(de) ?? {}, dt = r.object({
  children: r.custom(),
  strictVariantMatching: r.boolean().optional().describe(
    "Global default for strictVariantMatching. When true, prevents adding invalid size combinations to cart across all product lists and cards. Can be overridden per-component."
  )
}), Vt = G(
  dt,
  ({ children: e, strictVariantMatching: t }) => {
    const n = {
      strictVariantMatching: t
    };
    return /* @__PURE__ */ c(de.Provider, { value: n, children: e });
  }
), ae = h(
  void 0
), ue = h(void 0), le = h(
  []
), he = h(
  []
), at = Me.extend({
  product: r.custom(),
  relatedProducts: W.array().optional(),
  children: r.custom(),
  context: r.custom()
}), ut = G(
  at,
  (e) => {
    const t = ct(), n = e.relatedProducts ?? [], o = f(() => {
      const d = e.product.siblingProducts ?? [];
      d.find(
        (u) => u.productId === e.product.base.productId
      ) === void 0 && d.push(e.product.base);
      const a = JSON.parse(
        JSON.stringify(d)
      );
      return a.sort((u, p) => u.productId.localeCompare(p.productId)), a;
    }, [e.product.base, e.product.siblingProducts]), s = f(
      () => ({
        strictVariantMatching: t.strictVariantMatching ?? !1
      }),
      [t.strictVariantMatching]
    );
    let i;
    switch (e.product.dataModel) {
      case "simple":
        i = /* @__PURE__ */ c(it, { product: e.product, children: /* @__PURE__ */ c(T, { children: e.children }) });
        break;
      case "with_variants":
        i = /* @__PURE__ */ c(Je, { product: e.product, children: /* @__PURE__ */ c(T, { children: e.children }) });
        break;
      case "bundle":
        i = /* @__PURE__ */ c(nt, { product: e.product, children: /* @__PURE__ */ c(T, { children: e.children }) });
        break;
      default:
        return console.error(
          // @ts-expect-error This is a runtime check, so we print dataModel even if TS says it doesn't exist
          `Unknown product data model: "${e.product.dataModel}", check your mapper for bugs`
        ), null;
    }
    return /* @__PURE__ */ c(Y.Provider, { value: s, children: /* @__PURE__ */ c(ce.Provider, { value: e.context, children: /* @__PURE__ */ c(ue.Provider, { value: e.product.dataModel, children: /* @__PURE__ */ c(ae.Provider, { value: e.product.base, children: /* @__PURE__ */ c(le.Provider, { value: o, children: /* @__PURE__ */ c(he.Provider, { value: n, children: i }) }) }) }) }) });
  }
);
ut.displayName = "ProductProvider";
const m = () => {
  const e = l(ae);
  if (!e)
    throw new Error("useProductBase must be used within a ProductProvider");
  return e;
}, B = () => {
  const e = l(J);
  if (!e)
    throw new Error(
      "useProductPurchaseInfo must be used within a ProductPurchaseInfoProvider"
    );
  return e;
}, j = () => {
  const e = m(), t = B(), n = st(), { additionalData: o } = Pe();
  return f(
    () => ({
      productData: {
        base: e,
        purchaseInfo: t
      },
      context: n,
      additionalData: o
    }),
    [e.productId]
  );
}, Ot = (e) => {
  const t = pe(), n = j(), o = g({
    ...n,
    productData: { ...n.productData, base: e.productBase }
  });
  y(() => {
    o.current = {
      ...n,
      productData: { ...n.productData, base: e.productBase }
    };
  }, [n, e.productBase]);
  const s = me(() => {
    E.emit("productClicked", o.current), fe.emit("close", {});
  }, []), { productBase: i, ...d } = e;
  return /* @__PURE__ */ c(
    t,
    {
      ...d,
      onClick: s,
      href: e.productBase.slug
    }
  );
}, xt = () => {
  try {
    return m();
  } catch {
    return;
  }
}, Ct = ({
  attributes: e
}) => (t, n) => {
  if (!t || !e) {
    console.error("Attribute key is not set");
    return;
  }
  const o = e.find(
    (i) => i.attributeKey === t
  );
  if (!(typeof o > "u" || !n.safeParse(o.value).success))
    return o;
}, lt = ({
  attributes: e
}) => (t, n) => ht(e, t, n), ht = (e, t, n) => {
  if (!t) {
    console.error("Attribute key is not set");
    return;
  }
  const o = e.find(
    (i) => i.attributeKey === t
  );
  if (!(typeof o > "u" || !n.safeParse(o.value).success))
    return o;
}, Bt = (e, t) => {
  const n = m();
  return lt(n)(e, t);
}, jt = () => {
  const e = l(ue);
  if (!e)
    throw new Error(
      "useProductDataModel must be used within a ProductProvider"
    );
  return e;
}, Mt = () => O().optionGroups.some(
  (t) => t.options.some((n) => n.inventory.inStock)
), pt = (e, t) => e.filter((n) => {
  var s;
  const o = (s = n.attributes) == null ? void 0 : s.find(
    (i) => i.attributeKey === t.key
  );
  return t.value.includes(o == null ? void 0 : o.value);
}), Kt = (e) => {
  const {
    content: { media: t }
  } = m(), n = t.filter(Ie), o = e == null ? void 0 : e.attributeFilter;
  return o === void 0 ? n : pt(
    n,
    o
  );
}, Lt = () => B().inventory, Gt = () => H().some((t) => t.inventory.inStock), At = () => B().priceInfo, Rt = () => {
  const e = l(Q);
  if (!e)
    throw new Error(
      "useProductPurchaseStatus must be used within a ProductPurchaseStatusProvider"
    );
  return e;
}, _t = () => {
  const e = l(le);
  if (!e)
    throw new Error("useProductSiblings must be used within a ProductProvider");
  return e;
}, Nt = (e) => {
  var n;
  return (n = m().content.specifications) == null ? void 0 : n[e];
}, Ut = (e) => {
  const t = l(he);
  if (!t)
    throw new Error("useRelatedProducts must be used within a ProductProvider");
  return e ? t.filter(
    (n) => n.relationType === e
  ) : t;
}, bt = () => {
  const e = l(ie);
  if (!e)
    throw new Error("useBundleDispatch must be used within a BundleProvider");
  return e;
}, $t = () => te().itemId, zt = () => {
  const e = I(), t = bt(), n = (s, i) => {
    t({
      type: "SELECT_BUNDLE_PRODUCT_SIZE",
      payload: {
        sectionId: s,
        itemId: i
      }
    });
  }, o = (s, i) => {
    t({
      type: "SELECT_SECTION_PRODUCT",
      payload: {
        sectionId: s,
        product: i
      }
    });
  };
  return {
    sections: e.sections,
    selectSectionSize: n,
    selectSectionProduct: o
  };
}, Wt = () => {
  const e = l(re);
  if (!e)
    throw new Error(
      "useBundlePurchaseStatus must be used within a BundlePurchaseStatusProvider"
    );
  return e;
}, mt = (e) => {
  const { sections: t } = I(), n = t[e];
  if (!n)
    throw new Error(`Section '${e}' not found`);
  return n.activeProduct.product;
}, qt = (e) => {
  const { sections: t } = I(), n = mt(e), o = t[e];
  if (!o)
    throw new Error(`Section '${e}' not found`);
  return o.products.filter((s) => s !== n);
}, Ft = r.object({
  siblings: P.array().describe("The sibling products"),
  selectedSibling: P.describe("The selected sibling product")
}), Zt = r.object({
  label: r.string(),
  value: r.string(),
  icon: r.custom(() => !0).optional()
}), vt = r.object({
  label: r.string().describe("The label of the option, like 'Red' or 'Small'"),
  dimensionValue: r.string().describe("The value of the dimension, used when doing add to cart"),
  inventory: w,
  priceInfo: N.describe(
    "The price information for this option, useful in case of a price change depending on a selected option, like different materials"
  )
}), Ht = r.object({
  dimensionKey: r.string().describe("The dimension key"),
  dimensionLabel: r.string().describe("The dimension label"),
  options: r.array(vt)
});
export {
  nt as BundleProductProvider,
  Xe as BundlePurchaseStatusProvider,
  Vt as GlobalProductSettingsProvider,
  ot as IsBundleProvider,
  Ye as ProductBundlePurchaseProvider,
  Dt as ProductEventAdditionalDataProvider,
  Ot as ProductLink,
  ut as ProductProvider,
  x as ProductPurchaseInfoProvider,
  X as ProductPurchaseStatusProvider,
  it as ProductSimpleProvider,
  Fe as ProductVariantEventDispatcher,
  Ze as ProductVariantProvider,
  Ue as ProductVariantPurchaseProvider,
  T as ProductViewedEvent,
  Je as ProductWithVariantsProvider,
  D as attributeSchema,
  ie as bundleDispatchContext,
  ne as bundleProductContext,
  Be as bundlePurchaseInfoSchema,
  re as bundlePurchaseStatusContext,
  rt as bundleReducer,
  oe as bundleStateContext,
  pt as filterMediaOnAttributeValues,
  ht as getProductAttribute,
  q as getVariantFromOptions,
  se as isBundleContext,
  Ie as isProductMediaImage,
  Ve as previouslyDiscountedPriceSchema,
  _ as priceInfoSchema,
  Te as productBadgeSchema,
  ae as productBaseContext,
  P as productBaseSchema,
  je as productBundleSchema,
  Ce as productBundleSectionSchema,
  Ee as productCategorySchema,
  we as productContentSchema,
  ue as productDataModelContext,
  E as productEventEmitter,
  R as productMediaDimensionsSchema,
  ge as productMediaImageSchema,
  Se as productMediaSchema,
  ye as productMediaVideoSchema,
  Ht as productOptionGroupSchema,
  vt as productOptionSchema,
  ce as productProviderContextContext,
  at as productProviderSchema,
  J as productPurchaseInfoContext,
  V as productPurchaseInfoSchema,
  w as productPurchaseInventorySchema,
  ke as productPurchasePriceDiscountSchema,
  N as productPurchasePriceSchema,
  Q as productPurchaseStatusContext,
  he as productRelatedProductsContext,
  z as productSchema,
  Y as productSettingsContext,
  k as productSharedSchema,
  le as productSiblingProductsContext,
  De as productSiblingSchema,
  Ft as productSiblingsSchema,
  U as productSimpleSchema,
  Zt as productSpecificationSchema,
  Oe as productVariantDimensionSchema,
  ee as productVariantDispatchContext,
  Ke as productVariantReducer,
  xe as productVariantSchema,
  C as productVariantStateContext,
  Me as productViewModelSchema,
  $ as productWithVariantsSchema,
  W as relatedProductSchema,
  et as selectBundleProductSize,
  tt as selectSectionProduct,
  bt as useBundleDispatch,
  $t as useBundleItemId,
  zt as useBundleOptions,
  te as useBundleProduct,
  Qe as useBundlePurchaseInfo,
  Wt as useBundlePurchaseStatus,
  mt as useBundleSectionActiveProduct,
  qt as useBundleSectionProductAlternatives,
  I as useBundleState,
  ct as useGlobalProductSettings,
  kt as useIsBundle,
  xt as useMaybeProductBase,
  Ct as useMediaAttributeGetter,
  Bt as useProductAttribute,
  lt as useProductAttributeGetter,
  m as useProductBase,
  jt as useProductDataModel,
  Pe as useProductEventAdditionalData,
  j as useProductEventData,
  Mt as useProductHasOptionsInStock,
  Kt as useProductImages,
  Lt as useProductInventory,
  Gt as useProductIsInStock,
  Z as useProductOptions,
  At as useProductPriceInfo,
  st as useProductProviderContext,
  B as useProductPurchaseInfo,
  Rt as useProductPurchaseStatus,
  Ne as useProductSettings,
  _t as useProductSiblings,
  Nt as useProductSpecificationGroup,
  S as useProductVariant,
  F as useProductVariantDimensions,
  Le as useProductVariantDispatch,
  $e as useProductVariantEventData,
  O as useProductVariantState,
  H as useProductVariants,
  Ut as useRelatedProducts,
  Re as useSelectedVariantId,
  Ae as useVariantMissingDimensions,
  He as variantsToProductOptionGroups
};
